import React, { useState } from 'react';
import Modal from 'react-responsive-modal';
import { useSelector } from 'react-redux';
import { FiChevronDown } from 'react-icons/fi';
import { toast } from 'react-toastify';

import DestinationLabel from 'app/components/Kewlox/DestinationLabel';
import { PartOrder } from 'app/models/PartOrder';
import { useUpdatePartOrderMutation } from 'services/kewloxApi';
import { selectAllDestinations } from 'store/slices/destinationsSlice';

interface Props {
  partOrder: PartOrder;
}

const DestinationSelector = ({ partOrder }: Props) => {
  const destinations = useSelector(selectAllDestinations);
  const [isOpen, setIsOpen] = useState(false);
  const [destinationId, setDestinationId] = useState<number | ''>('');
  const [updatePartOrder, { isLoading }] = useUpdatePartOrderMutation();

  if (partOrder.index <= 1) {
    return <DestinationLabel destination={partOrder.destination} />;
  }

  const availableDestinations = destinations
    .filter(destination => destination.shop || destination.name === '@Home')
    .sort((a, b) => a.sorting - b.sorting);

  const close = () => {
    if (isLoading) return;
    setIsOpen(false);
    setDestinationId('');
  };

  const confirm = async () => {
    if (destinationId === '') return;

    try {
      await updatePartOrder({
        id: partOrder.id,
        destination: destinationId,
      }).unwrap();
      toast.success('La destination de la sous-commande a été modifiée.');
      close();
    } catch {
      toast.error(
        "La destination de la sous-commande n'a pas pu être modifiée.",
      );
    }
  };

  return (
    <>
      <button
        type="button"
        className="flex items-center cursor-pointer"
        onClick={() => setIsOpen(true)}
        aria-label="Changer la destination de la sous-commande"
      >
        <DestinationLabel destination={partOrder.destination} />
        <FiChevronDown />
      </button>
      <Modal open={isOpen} onClose={close} center>
        <div className="w-128 py-3 px-4">
          <h2 className="text-2xl mb-4">
            Changer la destination de la sous-commande
          </h2>
          <p className="mb-4">
            Cette modification concerne uniquement cette sous-commande.
          </p>
          <label htmlFor="part-order-destination" className="block mb-2">
            Destination
          </label>
          <select
            id="part-order-destination"
            className="border border-gray-300 w-full p-2"
            value={destinationId}
            onChange={event => setDestinationId(Number(event.target.value))}
          >
            <option value="">Sélectionner une destination</option>
            {availableDestinations.map(destination => (
              <option key={destination.id} value={destination.id}>
                {destination.name}
              </option>
            ))}
          </select>
          <div className="flex justify-end mt-5">
            <button
              type="button"
              className="border-2 text-lg px-2 py-1 rounded-sm mx-3"
              onClick={close}
              disabled={isLoading}
            >
              Annuler
            </button>
            <button
              type="button"
              className="bg-main text-white text-lg px-2 py-1 rounded-sm"
              onClick={confirm}
              disabled={
                isLoading ||
                destinationId === '' ||
                destinationId === partOrder.destinationNotAggregated.id
              }
            >
              {isLoading ? 'Modification...' : 'Confirmer'}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default DestinationSelector;
